import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Button, Box, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Modal from "../../../../../components/Modal/Modal";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  homeworkSubmissionTypes,
  resourceObjectTypes
} from "../../../../../constants";
import { ArrowBack } from "@mui/icons-material";
import { makeGetEntities } from "../../../../../redux/selectors/selectors";
import { useDispatch, useSelector } from "react-redux";
import { fetchFileFormats } from "../../../../../redux/actions/courseBuilderActions";
import {
  filesPreloading,
  uploadFile
} from "../../../../../services/fileUploadService";
import EditHomeworkSettings from "../../../../../components/CourseBuilder/EditHomework/EditHomeworkSettings";
import EditHomeworkInstructions from "../../../../../components/CourseBuilder/EditHomework/EditHomeworkInstructions";

import AddRepoItemVersionConfirmationModal from "../../../../../components/WarningModal/AddRepoItemVersionConfirmationModal";
import { isEmpty, omit } from "lodash";
import { validateEmail } from "../../../../../utils/validation";
import { useRepositoryHomework } from "../../../../../hooks/repositoryItems";
import LoadingSpinner from "../../../../../components/LoadingSpinner/LoadingSpinner";

const selector = makeGetEntities();

const getEditHomeworkSchema = t => {
  return Yup.object().shape({
    subscribedEmails: Yup.array().when("subscribed", {
      is: true,
      then: Yup.array().of(
        Yup.object().shape({
          email: Yup.string()
            .required(t("validationMessages.noEmptyEmails"))
            .test(
              "allValidEmails",
              t("validationMessages.allValidEmails"),
              function() {
                return validateEmail(this.parent.email);
              }
            )
        })
      )
    }),
    title: Yup.string()
      .required(t("validationMessages.fieldRequired"))
      .max(
        255,
        t("validationMessages.cannotExceedLength", {
          type: t("editHomework.titleFieldLabel"),
          maxLength: 255
        })
      ),
    instructions: Yup.string()
      .required(t("validationMessages.fieldRequired"))
      .test(
        "validInstructionsTextLength",
        t("validationMessages.cannotExceedLength", {
          type: t("editHomework.instructions"),
          maxLength: 800
        }),
        function() {
          return this.parent.instructionsTextOnlyLength <= 800;
        }
      )
  });
};

const EditHomeworkForm = ({
  onInstructionsViewChange,
  instructionsView,
  newHomework,
  onCloseModal,
  modalOpen,
  isLoading,
  disabled,
  instructionsAttachments,
  onAddAttachment,
  onRemoveAttachment
}) => {
  const {
    submitForm,
    setFieldTouched,
    validateForm,
    resetForm,
    values
  } = useFormikContext();
  const { t } = useTranslation();
  useEffect(() => {
    resetForm();
    onInstructionsViewChange(false);
  }, [modalOpen, newHomework]);

  const submitButtonText = t(
    `editHomework.${instructionsView ? "saveBtn" : "nextAddInstructionsBtn"}`
  );

  const { allFileFormats, userEmail } = useSelector(
    ({ courseBuilderState, userState }) => ({
      allFileFormats: selector(courseBuilderState, {
        type: "fileFormat"
      }),
      userEmail: userState.user.email
    })
  );

  const handleGoToInstructionsView = () => {
    validateForm().then(errors => {
      setFieldTouched("title");
      setFieldTouched(
        "subscribedEmails",
        [...new Array(values.subscribedEmails.length)].map(() => ({
          email: true
        }))
      );
      // Do not go to the instructions view if there is an error for title or subscribed emails
      if (isEmpty(omit(errors, "instructions"))) {
        onInstructionsViewChange(true);
      }
    });
  };

  return (
    <Form>
      <Modal
        scrollable
        disableEnforceFocus
        maxWidth="lg"
        fillHeight
        fullWidth
        showCloseIcon
        dialogTitle={
          <Box display="flex" alignItems="center">
            {instructionsView && (
              <Box m={-1} mr={1}>
                <IconButton
                  onClick={() => onInstructionsViewChange(false)}
                  disabled={disabled}
                  size="large"
                >
                  <ArrowBack />
                </IconButton>
              </Box>
            )}
            <Typography variant="h4" color="textPrimary">
              {t("editHomework.modalTitleEdit")}
            </Typography>
          </Box>
        }
        dialogContent={
          isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <LoadingSpinner padding={0} containerHeight={80} />
            </Box>
          ) : !instructionsView ? (
            <EditHomeworkSettings
              allFileFormats={allFileFormats}
              userEmail={userEmail}
            />
          ) : (
            <EditHomeworkInstructions
              disabled={disabled}
              instructionsAttachments={instructionsAttachments}
              onAddAttachment={onAddAttachment}
              onRemoveAttachment={onRemoveAttachment}
            />
          )
        }
        dialogActions={
          <>
            <Button color="primary" onClick={onCloseModal} disabled={disabled}>
              {t("editHomework.cancelBtn")}
            </Button>
            <Button
              disabled={disabled || isLoading}
              variant="contained"
              color="primary"
              data-qa="edit-homework-submit-btn"
              onClick={() =>
                !instructionsView ? handleGoToInstructionsView() : submitForm()
              }
            >
              {submitButtonText}
            </Button>
          </>
        }
        open={modalOpen}
        onClose={onCloseModal}
      />
    </Form>
  );
};

const AddNewHomeworkVersion = ({
  courseNames,
  onCancel,
  onClose,
  open,
  loading,
  newHomework,
  item
}) => {
  const { t } = useTranslation();
  const [instructionsView, setInstructionsView] = useState(false);
  const [currentAttachments, setCurrentAttachments] = useState([]);
  const [currentItem, setCurrentItem] = useState([]);
  const [attachmentsToAdd, setAttachmentsToAdd] = useState([]);
  const [homeworkData, setHomeworkData] = useState(null);
  const [submittedData, setSubmittedData] = useState({});
  const [isLoadingHomework, setIsLoadingHomework] = useState(null);
  const [isUpdatingHomework, setIsUpdatingHomework] = useState(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { updateHomework, bulkHomeworkAttachments } = useRepositoryHomework();

  useEffect(() => {
    dispatch(fetchFileFormats());
  }, []);

  useEffect(() => {
    if (open && newHomework) {
      setCurrentItem(item);
      setHomeworkData(newHomework);
    }
    if (!open) {
      setAttachmentsToAdd([]);
      setCurrentAttachments([]);
      setHomeworkData(null);
    }
  }, [newHomework, open]);

  useEffect(() => {
    if (homeworkData && homeworkData.homeworkAssignmentAttachments) {
      setCurrentAttachments(homeworkData.homeworkAssignmentAttachments);
    }
  }, [homeworkData]);

  const updateExistingHomework = async (newHomeworkData, newAttachments) => {
    updateHomework({
      ...newHomeworkData
    }).then(async updatedHomework => {
      setCurrentItem(updatedHomework.item);
      await bulkHomeworkAttachments(updatedHomework, newAttachments);
      setIsUpdatingHomework(false);
      return onClose(updatedHomework.item.id);
    });
  };

  const handleSubmitOrShowConfirmation = (
    newHomeworkData,
    uploadedInstructionAttachments
  ) => {
    setSubmittedData({
      homeworkData: newHomeworkData,
      instructionAttachments: uploadedInstructionAttachments
    });

    if (!!courseNames && courseNames.length > 0) {
      setConfirmationModalOpen(true);
    } else {
      handleSubmit(newHomeworkData, uploadedInstructionAttachments);
    }
  };

  // Once form is submitted, we send the request to create the new homework
  const handleSubmit = (newHomeworkData, uploadedInstructionAttachments) => {
    setConfirmationModalOpen(false);
    setIsLoadingHomework(true);
    const data = submittedData.homeworkData
      ? submittedData
      : {
          homeworkData: newHomeworkData,
          instructionAttachments: uploadedInstructionAttachments
        };

    const newAttachments = data.instructionAttachments.map(attachment => ({
      type: "file",
      id: attachment.fileId
    }));

    const existingAttachments = currentAttachments
      .filter(
        attachment =>
          attachment.type === resourceObjectTypes.homeworAssignmentAttachment
      )
      .map(attachment => ({ type: "file", id: attachment.blobId }));
    const submitHomework = () =>
      updateExistingHomework(data.homeworkData, [
        ...newAttachments,
        ...existingAttachments
      ]);

    submitHomework().then(() => {
      return setIsLoadingHomework(true);
    });
  };

  const handleAddAttachment = newAttachments => {
    const newFiles = newAttachments.map(attachment => ({
      name: attachment.name,
      url: URL.createObjectURL(attachment)
    }));
    setCurrentAttachments([...currentAttachments, ...newFiles]);
    setAttachmentsToAdd([...attachmentsToAdd, ...newAttachments]);
  };

  const handleRemoveAttachment = indexToRemove => {
    const updatedAttachments = [...currentAttachments];
    updatedAttachments.splice(indexToRemove, 1);
    setCurrentAttachments(updatedAttachments);
  };

  const closeConfirmation = () => {
    setConfirmationModalOpen(false);
  };

  const handleClose = () => {
    setConfirmationModalOpen(false);
    setIsUpdatingHomework(false);
    onCancel(currentItem.id);
  };

  return (
    <>
      <AddRepoItemVersionConfirmationModal
        courseNames={courseNames}
        open={confirmationModalOpen}
        onSubmit={handleSubmit}
        onClose={closeConfirmation}
      />
      <Formik
        initialValues={{
          ...(homeworkData && { id: homeworkData.id }),
          fileFormats:
            homeworkData && homeworkData.fileFormats
              ? homeworkData.fileFormats
              : [],
          subscribedEmails:
            homeworkData && homeworkData.subscribedEmails
              ? homeworkData.subscribedEmails
              : [],
          subscribed: homeworkData ? homeworkData.subscribed : false,
          instructions: homeworkData ? homeworkData.instructions : "",
          instructionsTextOnlyLength: 0,
          submissionType: homeworkData
            ? homeworkData.submissionType
            : homeworkSubmissionTypes.fileAndText,
          restrictFileFormat: homeworkData
            ? homeworkData.restrictFileFormat
            : false,
          title: homeworkData ? homeworkData.title : "",
          instructionsFileList: [],
          blobIds: []
        }}
        validationSchema={getEditHomeworkSchema(t)}
        enableReinitialize
        onSubmit={async values => {
          delete values.instructionsTextOnlyLength;
          setIsUpdatingHomework(true);
          const uploadFilePromises = attachmentsToAdd.map(attachment =>
            (async () => uploadFile(attachment))()
          );
          const [
            instructionHtmlUploads,
            ...uploadedInstructionAttachments
          ] = await Promise.all([
            filesPreloading(values.instructionsFileList, values.instructions),
            ...uploadFilePromises
          ]);
          values.instructionsFileList = instructionHtmlUploads;
          values.instructions = instructionHtmlUploads.content;
          handleSubmitOrShowConfirmation(
            values,
            uploadedInstructionAttachments
          );
        }}
      >
        {() => (
          <EditHomeworkForm
            onInstructionsViewChange={setInstructionsView}
            instructionsAttachments={currentAttachments}
            instructionsView={instructionsView}
            onCloseModal={handleClose}
            modalOpen={open}
            newHomework={newHomework}
            isLoading={isLoadingHomework}
            disabled={isUpdatingHomework}
            onAddAttachment={handleAddAttachment}
            onRemoveAttachment={handleRemoveAttachment}
          />
        )}
      </Formik>
    </>
  );
};

AddNewHomeworkVersion.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  homeworkData: PropTypes.shape({
    id: PropTypes.string,
    subscribed: PropTypes.bool,
    type: PropTypes.string,
    title: PropTypes.string,
    submissionType: PropTypes.string,
    restrictFileFormat: PropTypes.bool,
    instructions: PropTypes.string,
    trainingObjectId: PropTypes.string,
    fileFormats: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        mime: PropTypes.string,
        category: PropTypes.string,
        extension: PropTypes.string
      })
    ),
    subscribedEmails: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        email: PropTypes.string
      })
    )
  }),
  trainingObjectId: PropTypes.string
};

export default AddNewHomeworkVersion;
